package terraultima

import kotlinx.serialization.Serializable

// ============================== CONTENT SCHEMA ==============================
// Everything below is authored data (JSON). Keep it small enough to fit on two
// pages — see docs/04-CONTENT-AUTHORING.md and criterion C9 in D001.

/**
 * A capability is a VERB THE COLONY CAN PERFORM.
 *
 * [short] is the name the player sees, and it is the ONLY name they ever see: in the CAN DO list,
 * in "LOCKED — needs X", and in an option hint. Two playtests found eleven separate readers stuck
 * on `LOCKED - needs records` while `archive` sat in CAN DO — two different capabilities whose
 * lowercased ids are both plausible words for "the thing that remembers". Deriving the label from
 * the id gave the player nouns to confuse; naming the verb does not.
 */
@Serializable
data class Capability(val id: String, val verb: String, val note: String = "", val short: String = "")

@Serializable
data class Module(
    val id: String,
    val name: String,
    val mass: Double = 1.0,            // tonnes
    val cost: Double = 1.0,            // arbitrary money units
    val complexity: Double = 0.3,      // 0..1 -> drives failure risk (designer's param)
    val grants: List<String> = listOf(),
    val powerDraw: Double = 0.0,       // kW
    val powerGen: Double = 0.0,        // kW
    val phase: List<String> = listOf("ANY"),
    val modifiers: Map<String, Double> = mapOf(), // passive coefficients (D002 §6)
    val tags: List<String> = listOf()
)

@Serializable
data class Build(
    val id: String,
    val name: String,
    val modules: List<String>,
    val adults: Int = 30,
    val children: Int = 0,
    val cryo: Int = 0,
    /** Pre-flight testing spend, 0..1. Designer's mechanic: money spent here buys down the
     *  failure risk that `Module.complexity` creates. Per-module in the real game; scalar here. */
    val testing: Double = 0.5,
    /** Years of cruise before the target is reached. A build choice, not a constant. */
    val arrivalYear: Int = 2,
    val note: String = ""
)

@Serializable
data class Planet(
    val id: String,
    val name: String,
    val gravity: Double = 1.0,          // g
    val pressure: Double = 1.0,         // bar
    val temp: Double = 288.0,           // K mean
    val water: Double = 0.5,            // 0..1 accessible water
    val flora: Double = 0.0,            // 0..1
    val fauna: Double = 0.0,            // 0..1
    val seismic: Double = 0.2,          // 0..1
    val toxicity: Double = 0.2,         // 0..1 soil/atmo chemistry hostility
    val insolation: Double = 1.0,       // relative to Earth
    val dayLength: Double = 24.0,       // hours; known from Earth by observation
    /**
     * Metal richness, 0..1. The first planet parameter that is purely an OPPORTUNITY rather than a
     * hazard. Every other number here describes something trying to kill you; a world also has to
     * be good for something, or there is nothing to build and the whole game is triage.
     */
    val ore: Double = 0.3,
    val peculiarities: List<String> = listOf()
) {
    /**
     * "Ochre", from "Ochre, 0.38 g". The corpus names worlds the way a mission planner would, with
     * the headline parameter welded to the name; in a sentence — "in orbit above Ochre" — that
     * parameter is in the way, and the full name is still what a table heading wants.
     */
    val shortName: String get() = name.substringBefore(",").trim()
}

/**
 * Alternate wording for an event that legitimately recurs.
 *
 * A colony faces many dust storms; it does not have two first schisms. The failure is not recurrence
 * but *identical* recurrence — the same paragraph twice reads as a bug even when the situation is
 * real. Recurring events therefore carry variants, and the mechanics stay the same underneath.
 */
@Serializable
data class Variant(val title: String = "", val body: String)

/**
 * One way an option can turn out.
 *
 * [requires] is what makes the BUILD change the consequence rather than only the menu — red-team
 * F9, *"build-gating is decoration: the lists differ, the behaviour does not."* Every one of D002's
 * five layers acts on the menu; nothing made the build change the *result* of the two mandatory
 * ungated options, and those are what a player takes 78% of the time.
 *
 * The designer's correction, which shaped this field: it is *"not always a probability difference,
 * it's also an outcome difference. Being in orbit you can use the drone if you had it, updating
 * planet info with a lot of info. Or you can land — those are different outcomes."* So a branch is
 * not a re-weighted version of the same result; it is a different result, with its own text and its
 * own effects. Scaling `p` alone could never express "you learn the air pressure" versus "you learn
 * nothing and have committed".
 *
 * Branches whose [requires] fail are removed from the draw and the rest are renormalised, so an
 * author writes what CAN happen and the state decides which of those is in play. Leave [requires]
 * empty for a branch that is always available.
 */
@Serializable
data class Outcome(
    val p: Double = 1.0,
    val text: String = "",
    val effects: List<String> = listOf(),
    /** Conditions in the same language as `Option.requires`. Empty = always a candidate. */
    val requires: List<String> = listOf()
)

/**
 * WHAT A LOCKED OPTION DOES TO THE SCREEN.
 *
 * Every gated option used to render the same way when its gates failed: in place, numbered, with a
 * lock and a reason. That is right for some and actively confusing for others, and the difference
 * is not something the engine can work out on its own.
 *
 * The two ends of it, from the same playtest sitting:
 *
 *  - "Descend on the site chosen from orbit" needs 2 km/s of spare fuel. A player in orbit with an
 *    empty tank MUST see that the ordinary way down exists and why it is shut, or the screen reads
 *    as a game that never offered it. [SHOW].
 *  - "Power first — nothing else works without it" is gated on power actually being tight. Shown
 *    locked to a colony with power to spare it invites "why can I not prioritise power?", which is
 *    a question about a thing that is not a problem. [HIDE].
 *  - "Send a crewed party overland" needs a rover this expedition did not bring. Nothing about this
 *    century will change that, so the full block is a standing advertisement for another run — but
 *    the NAME is worth having, because choosing what to bring is the game before the game. [NAME]:
 *    listed under the options as a name and nothing else.
 *
 * [AUTO] is the default and picks between SHOW and NAME by what is actually missing: an option
 * locked only by equipment is a different-ship option (NAME); one locked by the colony's state or
 * its own history is a fact about right now, and right now is what the player is reading (SHOW).
 */
enum class WhenLocked { AUTO, SHOW, NAME, HIDE }

@Serializable
data class Option(
    val label: String,
    val requires: List<String> = listOf(),
    val tags: List<String> = listOf(),
    val effects: List<String> = listOf(),
    val outcomes: List<Outcome> = listOf(),
    val hint: String = "",             // player-facing statement of intent & cost (Clarity A3)
    /** "auto" | "show" | "name" | "hide" — see [WhenLocked]. Only ever consulted when locked. */
    val whenLocked: String = "auto"
) {
    val lockedAs: WhenLocked get() =
        runCatching { WhenLocked.valueOf(whenLocked.uppercase()) }.getOrDefault(WhenLocked.AUTO)
}

/**
 * OPPORTUNITY exists because of a measured flaw: `tension` was both the thing a good player
 * suppresses AND the pacing denominator, so playing well bought a *quieter* game — the best policy
 * got the fewest decisions (red-team F3). A colony that is not in trouble should be offered
 * ambitions, not silence.
 */
enum class EventKind { DILEMMA, SYMPTOM, CRISIS, NOTICE, OPPORTUNITY }

@Serializable
data class GameEvent(
    val id: String,
    val title: String,
    val kind: String = "DILEMMA",
    val phase: List<String> = listOf("ANY"),
    val requires: List<String> = listOf(),
    val pressures: Map<String, Double> = mapOf(),
    val weight: Double = 1.0,
    val cooldownYears: Int = 8,
    val once: Boolean = false,
    /**
     * "I can stand in for beat X of a chain."
     *
     * A `schedule:` names one event, so a consequence arc played the same middle every time. The
     * designer's intent (D002 §0) is that *"some parts of a chain would be pluggable / variative"* —
     * the same opening leading to different middles depending on what the colony is and what it
     * did, which is a great deal of variety for very little content.
     *
     * An event with `stageOf = "reckoning"` is a candidate whenever a thread for `reckoning` comes
     * due. The engine gathers the base beat plus every stand-in, keeps the ones whose own gates
     * pass, and draws among them — so the branch is chosen by the state of the colony rather than
     * authored as a tree.
     */
    val stageOf: String = "",
    val symptomOf: String = "",
    val body: String = "",
    val slots: List<String> = listOf(),
    /** Alternate wordings, cycled by how many times this event has already fired. */
    val variants: List<Variant> = listOf(),
    val options: List<Option> = listOf(),
    val source: String = "PRESSURE"    // PRESSURE | SIM | SCHEDULED
) {
    val kindE: EventKind get() = EventKind.valueOf(kind)
}

/**
 * Why you are leaving Earth. Per `docs/05-EXPEDITION-SETUP.md` §1 a catastrophe must be a mechanical
 * fingerprint, not flavour: it sets what the colony launches carrying, socially as well as materially.
 * MVP implements the subset that costs nothing to wire — starting pressures, starting flags, and
 * whether Earth is still there to talk to.
 */
@Serializable
data class Catastrophe(
    val id: String,
    val name: String,
    val line: String,                       // "You are leaving Earth because..."
    val detail: String = "",
    val yearsToLaunch: Int = 5,
    val earthPersistsYears: Int = 20,       // after this, Earth stops answering
    val startPressures: Map<String, Double> = mapOf(),
    val startFlags: List<String> = listOf(),
    val teaches: String = "",               // what this motive is FOR, shown in the setup review
    val build: String = "",                 // the pre-generated expedition this motive launches
    val planet: String = ""
)

@Serializable
data class Corpus(
    val capabilities: List<Capability> = listOf(),
    val modules: List<Module> = listOf(),
    val builds: List<Build> = listOf(),
    val planets: List<Planet> = listOf(),
    val events: List<GameEvent> = listOf(),
    val catastrophes: List<Catastrophe> = listOf()
)

// ============================== RUNTIME STATE ==============================

enum class Phase { SPACE, ORBIT, SURFACE }

val PRESSURES = listOf(
    "inequality", "dissent", "faith", "aiAutonomy", "secrecy",
    "earthEstrangement", "entropy", "ecoStrain", "xenoExposure", "founderDrift"
)

val GAUGES = listOf("morale", "health", "cohesion", "legitimacy", "competence")

/**
 * The five-rung ladder a gauge is READ as, and the player-facing name of each gauge.
 *
 * One source of truth, because two things now depend on it: the interface prints the word plus its
 * rung ("willing morale 4/5"), and the change ledger reports a CROSSING ("unity is uneasy now, not
 * together"). Iteration 2 watched unity and standing each drop a rung between screens with nothing
 * reported, because the ledger had no idea the ladder existed.
 *
 * A band word must never be a form of its own label — "skilled skill" was flagged in two playtests,
 * which is why competence reads "capable" rather than "skilled".
 */
val GAUGE_BANDS = mapOf(
    "morale" to listOf("mutinous", "sullen", "steady", "willing", "devoted"),
    "health" to listOf("dying", "sick", "strained", "sound", "robust"),
    "cohesion" to listOf("fracturing", "divided", "uneasy", "together", "united"),
    "legitimacy" to listOf("defied", "questioned", "tolerated", "respected", "unquestioned"),
    "competence" to listOf("helpless", "struggling", "adequate", "capable", "expert")
)

val GAUGE_NAMES = mapOf(
    "morale" to "morale", "health" to "health", "cohesion" to "unity",
    "legitimacy" to "authority", "competence" to "skill"
)

/**
 * WHAT THE WORD ON THE LADDER IS A WORD ABOUT.
 *
 * "tolerated standing" was read back verbatim as unintelligible, and it is: two abstract nouns, one
 * of which is a five-rung band word and the other a gauge nobody named. The band word is the right
 * register — a gauge is a word, never a number (D003 §3.2) — but a word needs the thing it is a
 * word about, once, somewhere the player can find it. The gauge is "authority" now, not "standing":
 * "standing" names a quantity of nothing, and the four gauges beside it are all plain nouns.
 *
 * Also what makes an option's price readable. "more skill" is a gain in something
 * the player has never been told the meaning of; the gloss is where "skill" acquires one.
 */
/**
 * The same five gauges as they read on an OPTION'S PRICE LINE, where there is no ladder and no
 * gloss beside them. "less skill" was asked about directly — skill at what? — because four of these
 * are ordinary English on their own and "skill" is not.
 */
val GAUGE_PRICE_NAMES = mapOf(
    "morale" to "morale", "health" to "health", "cohesion" to "unity",
    "legitimacy" to "authority", "competence" to "skill with the machines"
)

val GAUGE_MEANS = mapOf(
    "morale" to "whether people are willing",
    "health" to "how well and how fed the colony is",
    "cohesion" to "whether it holds together as one people",
    "legitimacy" to "how far people accept your say",
    "competence" to "how well they work the machines they have"
)

// Opposed identity axes: name -> (positiveTag, negativeTag). Derived from the decision ledger only.
val AXES = mapOf(
    "cruelty" to Pair("harsh", "humane"),
    "hierarchy" to Pair("hierarchical", "egalitarian"),
    "piety" to Pair("pious", "secular"),
    "boldness" to Pair("bold", "cautious"),
    "independence" to Pair("independent", "earthbound"),
    "machineTrust" to Pair("machineTrusting", "humanOnly"),
    "secretiveness" to Pair("secretive", "open"),
    "expansion" to Pair("expansionist", "conservationist")
)

/**
 * A pending multi-stage situation: the game's MEMORY, and the ONLY place event-local parameters may
 * live (D003 §3.6). `schedule:evt in 3..8y` opens one of these.
 *
 * Why not globals: a writer who wants "how many times has this particular argument resurfaced?"
 * would otherwise add a global counter, and by event #300 the parameter list is unreadable. Scoped
 * vars give unlimited local state at zero cost to the global namespace, and are garbage-collected
 * when the thread resolves.
 *
 * `vars` and `bindings` are INHERITED when one stage schedules the next, so a three-stage arc can
 * accumulate state and keep talking about the same person.
 */
/**
 * Something the colony is BUILDING, over years.
 *
 * Events happen to the player; an ambition is the one thing the player does *to* the world. Without
 * this the game is loss-allocation end to end — a cold reader put it exactly: "you cannot build an
 * engine here, so there is no compounding decision" (decisions/007). An ambition consumes slack over
 * years, shows visible progress, and pays off with something lasting, which is what makes a quiet
 * decade feel like building rather than waiting.
 */
class Ambition(val id: String, var monthsDone: Int = 0)

/** Ambition definitions. Small enough to live in code; move to JSON past a dozen. */
data class AmbitionDef(
    val id: String,
    val title: String,
    val years: Int,
    val sparesPerYear: Double,
    val reward: List<String>,        // effects applied on completion
    val done: String                 // the line the player reads when it lands
)

val AMBITIONS = listOf(
    AmbitionDef("hall", "A hall to gather in", 3, 12.0,
        listOf("gauge.cohesion += 14", "gauge.morale += 10", "flag:has_hall = true"),
        "The hall is finished. It has no function and people are in it constantly."),
    AmbitionDef("second_well", "A second well", 2, 14.0,
        listOf("res.water += 90", "pressure.inequality -= 0.12", "flag:second_well = true"),
        "The second well comes in. Nobody has argued about water for a month."),
    AmbitionDef("apprentices", "A generation who can improvise", 4, 4.0,
        listOf("gauge.competence += 16", "pressure.entropy -= 0.12", "flag:apprenticed = true",
               "grant:FABRICATE"),
        "The apprentices finish. For the first time in years somebody can fix a thing nobody wrote down."),
    AmbitionDef("long_study", "Twelve years of looking at one thing", 12, 2.0,
        listOf("res.data += 60", "gauge.competence += 10", "pressure.ecoStrain -= 0.15",
               "flag:long_study = true"),
        "The study ends. It answered its question, and the answer turns out to matter."),

    // --- built on what the planet is actually good for. These are the constructive long games:
    // --- not "survive the place" but "make the place pay for itself".
    // These four end in a MODULE, which is the only way the colony's CAN DO list ever grows.
    // Two playtests spent 65 and 98 years watching gated options they could never reach, because
    // the capability set could shrink and never grow. A six-year undertaking that ends in "you can
    // smelt metal now" is what makes the whole locked half of the menu worth wanting.
    AmbitionDef("iron_economy", "An ironworks in the ridge", 6, 8.0,
        listOf("flag:ironworks = true", "gauge.competence += 8", "pressure.entropy -= 0.15",
               "res.spares += 120", "install:ironworks"),
        "The ironworks comes in. For the first time the colony makes more metal than it consumes."),
    AmbitionDef("deep_water", "Tap the deep ice", 5, 10.0,
        listOf("flag:deep_water = true", "res.water += 200", "pressure.ecoStrain -= 0.15",
               "gauge.health += 6", "install:deep_bore"),
        "The deep bore reaches ice. Water stops being a thing anyone counts."),
    AmbitionDef("geothermal", "Heat from underneath", 5, 14.0,
        // NOT `res.energy += 120`: energy is a COMMITMENT that decays over two years (D008), so
        // a permanent heat source expressed that way quietly evaporates. A permanent change to the
        // power balance goes through physics, which means a module.
        listOf("flag:geothermal = true", "install:geothermal_loop", "pressure.entropy -= 0.10"),
        "The geothermal loop runs. The ground that keeps shaking you now keeps you warm."),
    AmbitionDef("bio_industry", "Learn to use what grows here", 8, 6.0,
        listOf("flag:bio_industry = true", "res.food += 150", "pressure.ecoStrain -= 0.25",
               "res.data += 40", "grant:LAB_BIO"),
        "The native cultures take. Something that grew here is now feeding people.")
)

class Thread(
    val familyId: String,
    var stage: Int = 0,
    var dueMonth: Int = -1,
    val vars: MutableMap<String, Double> = mutableMapOf(),
    val bindings: MutableMap<String, String> = mutableMapOf()
)

class FlagVal(val setAt: Int, val expiresAt: Int = -1, val value: Double = 1.0)

class WorldState(val build: Build, val planet: Planet, val mission: String, val catastrophe: String,
                 val cata: Catastrophe? = null) {
    var month = 0
    val year get() = month / 12
    var phase = Phase.SPACE
    var alive = true
    var endReason = ""
    var landfallMonth = -1

    // -------- resources (visible, exact) --------
    var adults = build.adults.toDouble()
    var children = build.children.toDouble()
    var elders = 0.0
    var cryo = build.cryo.toDouble()
    var food = 0.0            // person-months
    var water = 0.0           // person-months of closed-loop margin
    var spares = 0.0
    var medicine = 0.0
    var deltaV = 0.0          // km/s
    var cultivated = 0.0      // m2
    var data = 0.0
    /**
     * `res.energy` — a signed POWER COMMITMENT in kW, not a stock (D008).
     *
     * Positive = power freed (draw falls); negative = power committed to something (draw rises).
     * It decays with a two-year half-life, because that is what the corpus actually claims: 29 of
     * the 54 options that write it name a finite span — "a season of instrument time", "three weeks
     * of the shop", "five years of the drill" — and exactly one claims permanence. A commitment
     * ends when the work does.
     *
     * It was `energyStore`, a stock that `Sim.kt` never read, no screen ever showed and nothing
     * gated on — 56 effects across a third of the corpus, all of them inert, and clamped at zero so
     * that every `res.energy -= N` was silently free as well. See `docs/decisions/008`.
     *
     * PERMANENT changes to the material world are not written here: they go through a flag physics
     * reads (`flag:geothermal`), a module, or an ambition.
     */
    var powerCommit = 0.0

    val popTotal get() = adults + children + elders

    // -------- gauges (visible as bands) --------
    val gauge = GAUGES.associateWith { 60.0 }.toMutableMap()

    // -------- pressures (hidden 0..1) --------
    val pressure = PRESSURES.associateWith { 0.05 }.toMutableMap()

    // -------- identity ledger -> axes (hidden -1..1) --------
    val tagLedger = mutableMapOf<String, Double>()

    // -------- flags / threads --------
    val flags = mutableMapOf<String, FlagVal>()
    val threads = mutableListOf<Thread>()
    val ambitions = mutableListOf<Ambition>()


    /**
     * Incidents the SIMULATION has just caused and which have not yet been shown to the player.
     * Physics *pushes* to this; the director drains it. Polling "is a module broken?" every month
     * re-fires the same event forever while the module stays broken; emitting once, at the moment
     * it breaks, is both correct and how shipped games ended up doing it (Paradox replaced its
     * mean-time-to-happen polling with hook-emitted events for the same reason).
     */
    val pendingIncidents = mutableListOf<Pair<String, Map<String, String>>>()

    // -------- modules --------
    val installed = build.modules.toMutableList()
    val moduleHealth = build.modules.associateWith { 1.0 }.toMutableMap()

    /**
     * Capabilities the colony acquired by INSTITUTION rather than by hardware.
     *
     * Two playtests ran 65 and 98 years without the CAN DO list ever gaining an entry, because the
     * only writable module state was `damage:`. The set could shrink and never grow, so every
     * capability-gated option was permanently dangled — one reviewer, watching a colony die of
     * thirst on a world with 80% measured water while "Mine ice" sat locked: *"that's not tension,
     * it's a taunt."*
     *
     * Kept separate from [installed] because the two are different things and a reader should be
     * told which one they got. A smelter is mass and power; an elected council is a decision that
     * cannot be un-decided cheaply, and it grants VOTE just as really.
     */
    val granted = mutableSetOf<String>()

    /**
     * WHERE THE COLONY SETTLES, as opposed to where it is this month — moved only by decisions.
     *
     * The gauges chase a target computed entirely from the hidden pressures, at 6% a month. So an
     * option's `gauge.legitimacy += 9` was gone inside a year, and a hundred years of governing
     * moved the five visible gauges not at all. Two playtests caught it from the outside without
     * ever seeing this file: *"the game made me a specific promise, twice, and the number on my own
     * status line says it didn't keep it. Once a player notices that, every 'gains:' line becomes
     * noise."* Another, on a colony that read `mutinous` for eighteen unbroken years: *"it suggests
     * the morale stat is decorative, not load-bearing."*
     *
     * A decision now moves the gauge AND the level it returns to. That is the game's core promise
     * (`00-VISION.md`) expressed in the one place the player can actually watch: not "your choices
     * jolt a number for a season" but "your choices are why this colony is the kind of colony that
     * is trusted".
     *
     * Bounded and slowly forgotten — half-life about fourteen years — because a colony that could
     * bank standing for ever would let a good first decade buy an unaccountable second century.
     */
    val gaugeShift = GAUGES.associateWith { 0.0 }.toMutableMap()

    /**
     * What changed while the player was not being asked. Physics writes here; the interface drains
     * it into the year report. Fifty people died between two screens in one playtest and the entire
     * account of it was the line "- year 54"; a colony that loses eight capabilities to a brownout
     * and is told nothing reads as a bug, not a story beat.
     */
    val changes = mutableListOf<String>()

    /**
     * How many gambles resolved, and how many of those resolved down a branch the BUILD chose.
     *
     * F9's own proposed metric (K4b, total variation between chosen-option distributions) turns out
     * to be blind to F9's own proposed fix: conditional outcomes change what HAPPENS, not what is
     * picked, and no policy in `Bots.kt` reads an outcome branch — tag-driven bots choose by
     * identity and `greedy` by the immediate visible effect. So the metric cannot move however well
     * the fix works. These two counters measure the mechanism instead of its downstream shadow.
     */
    var gamblesResolved = 0
    var gamblesBuildDetermined = 0

    /** Last month's watched quantities and capability set, for the diff that fills [changes]. */
    var watchedLast: Map<String, Double>? = null
    var capsLast: Set<String> = emptySet()

    // -------- bookkeeping for director + metrics --------
    val lastFired = mutableMapOf<String, Int>()
    val fireCount = mutableMapOf<String, Int>()
    val capOffered = mutableMapOf<String, Int>()     // spotlight: times a cap gated an OFFERED option
    val capUsed = mutableMapOf<String, Int>()        // times the player actually took such an option
    val pressureLastExpressed = mutableMapOf<String, Int>()
    /** pressure -> months at which a symptom of it was reported. The two-warnings interlock. */
    val symptomWarnings = mutableMapOf<String, MutableList<Int>>()
    val log = mutableListOf<String>()
    /** Per-parameter estimate error, fixed at setup so an estimate stays wrong the same way. */
    val estimateNoise = mutableMapOf<String, Double>()
    var absorbed = 0            // failures the colony handled without asking the player
    var shownFailures = 0       // failures escalated to the player
    /**
     * Month 0, NOT a negative sentinel. At -99 the drought rail (MAX_DECISION_GAP) was already
     * tripped on the first month of every game, which forced a decision in month 0 AND ignored
     * every deck's demand floor to find one — so the opening question was drawn from all ten
     * pressure decks at their launch values, i.e. from whatever the colony was least motivated
     * to be asked about. That is how a petition of thirty names became a year-0 event.
     */
    var lastDecisionMonth = 0
    /** Set the month an ambition completes, so the interface can announce it. */
    var pendingAmbition: AmbitionDef? = null
    // Counters for the real loss conditions. "Still has two people" is not survival.
    var peakPop = 0.0
    /** People born in the colony rather than launched with it — the second-generation goal. */
    var bornHere = 0.0
    /**
     * Deaths, so that an event whose fiction begins "an avoidable death" can require one.
     * `deaths` includes the actuarial background; `deathsNotable` counts only the deaths that
     * arrived as an event — a breach, a dose, a bad decision — which is what an inquiry is about.
     * Both are fractional because the population is.
     */
    var deaths = 0.0
    var deathsNotable = 0.0
    /**
     * A yearly snapshot, so the interface can show DIRECTION rather than only level.
     * "Food 2,724" implies nothing; "food 2,724, falling for three years" implies a goal, and the
     * designer's point is that the goal should form in the player's head rather than be listed.
     */
    val yearly = mutableListOf<DoubleArray>()   // food, water, spares, pop, powerRatio, medicine

    /**
     * (year, population, food) once a year, for the whole run and never trimmed.
     *
     * [yearly] keeps eight years so the status panel can draw a trend; an epilogue needs the
     * century. Two floats a year for a hundred years is nothing, and without it the ending cannot
     * name the famine that defined the run — which is exactly what one playtest reported.
     */
    val history = mutableListOf<Triple<Int, Double, Double>>()
    var lowPowerMonths = 0
    var noFutureMonths = 0
    var starvingMonths = 0
    val trace = mutableListOf<TraceRow>()
    var crewNames = mutableListOf<String>()

    fun s2(): Int = trace.count { it.kind != "SYMPTOM" }

    fun axis(name: String): Double {
        val (pos, neg) = AXES[name] ?: return 0.0
        val a = tagLedger[pos] ?: 0.0
        val b = tagLedger[neg] ?: 0.0
        if (a + b < 1e-9) return 0.0
        return (a - b) / (a + b) * kotlin.math.min(1.0, (a + b) / 6.0)
    }

    fun hasFlag(n: String): Boolean {
        val f = flags[n] ?: return false
        if (f.expiresAt in 0 until month) { flags.remove(n); return false }
        return true
    }
}

class TraceRow(
    val month: Int,
    val eventId: String,
    val title: String,
    val kind: String,
    val theme: String,
    val optionsOffered: Int,
    val gatedOffered: List<String>,
    val chosen: String,
    val chosenTags: List<String>,
    val deckSize: Int,
    val pressureSnapshot: Map<String, Double>
)
